import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AudioPlayer = ({ text, language }) => {
  const { audioSpeed, setAudioSpeed } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const utteranceRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(0);
  const intervalRef = useRef(null);

  const speedOptions = [
    { value: 0.75, label: '0.75x' },
    { value: 1.0, label: '1x' },
    { value: 1.25, label: '1.25x' },
  ];

  const estimateDuration = useCallback((text, rate) => {
    const words = text.split(/\s+/).length;
    const baseWPM = 150;
    const adjustedWPM = baseWPM * rate;
    return (words / adjustedWPM) * 60;
  }, []);

  const stopAudio = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const playAudio = useCallback((startFrom = 0) => {
    if (!text) return;

    stopAudio();

    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    if (language === 'tc') {
      utterance.lang = 'zh-HK';
    } else if (language === 'sc') {
      utterance.lang = 'zh-CN';
    } else {
      utterance.lang = 'en-US';
    }

    const voices = window.speechSynthesis.getVoices();
    let selectedVoice = null;
    if (language === 'tc') {
      selectedVoice = voices.find(v => v.lang.includes('zh-HK') || v.lang.includes('zh-TW'));
    } else if (language === 'sc') {
      selectedVoice = voices.find(v => v.lang.includes('zh-CN'));
    } else {
      selectedVoice = voices.find(v => v.lang.includes('en-US') || v.lang.includes('en-GB'));
    }
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    utterance.rate = audioSpeed;
    utterance.pitch = 1;

    const estimated = estimateDuration(text, audioSpeed);
    startTimeRef.current = performance.now() - (startFrom * 1000);
    setCurrentTime(startFrom);

    utterance.onstart = () => {
      setIsPlaying(true);
      intervalRef.current = setInterval(() => {
        const elapsed = (performance.now() - startTimeRef.current) / 1000;
        if (elapsed <= estimated) {
          setCurrentTime(elapsed);
        }
      }, 100);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    utterance.onerror = () => {
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    window.speechSynthesis.speak(utterance);
  }, [text, language, audioSpeed, stopAudio, estimateDuration]);

  useEffect(() => {
    if (text) {
      const estimated = estimateDuration(text, audioSpeed);
      setDuration(estimated);
    }
    return () => {
      stopAudio();
    };
  }, [text, audioSpeed, estimateDuration, stopAudio]);

  const togglePlay = () => {
    if (isPlaying) {
      stopAudio();
    } else {
      playAudio(currentTime);
    }
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    setCurrentTime(newTime);
    if (isPlaying) {
      playAudio(newTime);
    }
  };

  const handleSpeedChange = (newSpeed) => {
    setAudioSpeed(newSpeed);
    setShowSpeedOptions(false);
    if (isPlaying) {
      const current = currentTime;
      stopAudio();
      setTimeout(() => playAudio(current), 100);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const currentSpeedLabel = speedOptions.find(s => s.value === audioSpeed)?.label || '1x';

  return (
    <div className="flex items-center gap-3">
      {/* Play/Pause Button - Smaller */}
      <button
        onClick={togglePlay}
        className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 hover:bg-white/90 transition-colors"
        data-testid="audio-play-button"
      >
        {isPlaying ? (
          <Pause className="w-4 h-4 text-black" />
        ) : (
          <Play className="w-4 h-4 text-black ml-0.5" />
        )}
      </button>

      {/* Progress Bar - Thinner */}
      <div className="flex-1 space-y-0.5">
        <div
          ref={progressRef}
          className="h-1.5 bg-white/20 rounded-full cursor-pointer relative"
          onClick={handleProgressClick}
          data-testid="audio-progress-bar"
        >
          <div
            className="h-full bg-white rounded-full relative"
            style={{ width: `${progressPercentage}%` }}
          >
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg border border-black/20"
              style={{ transform: 'translate(50%, -50%)' }}
            />
          </div>
        </div>
        <div className="flex justify-between text-[10px] text-white/60">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Speed Control - Dropdown */}
      <div className="relative flex-shrink-0">
        <button
          onClick={() => setShowSpeedOptions(!showSpeedOptions)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
          data-testid="speed-selector"
        >
          {currentSpeedLabel}
          <ChevronDown className={`w-3 h-3 transition-transform ${showSpeedOptions ? 'rotate-180' : ''}`} />
        </button>
        
        {showSpeedOptions && (
          <div className="absolute bottom-full mb-1 right-0 bg-zinc-800 rounded-lg shadow-lg border border-white/20 overflow-hidden z-10">
            {speedOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSpeedChange(option.value)}
                className={`block w-full px-4 py-2 text-sm text-left transition-colors ${
                  audioSpeed === option.value
                    ? 'bg-white text-black'
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid={`speed-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;
